import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  MessageCircle, DollarSign, Calculator, Info, CheckCircle, 
  AlertCircle, MapPin, Truck, Package, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { generateWhatsAppLink, trackWhatsAppClick } from '@/utils/whatsappTemplates';
import { 
  calculateEstimate, 
  getAllSuburbNames, 
  getSuburbInfo,
  pricingConfig,
  tierMultipliers,
  addOnPricing 
} from '@/data/pricingConfig';

const Pricing = () => {
  const [formData, setFormData] = useState({
    serviceType: 'delivery',
    pickupSuburb: '',
    dropoffSuburb: '',
    tier: 'TODAY',
    helpers: 'none',
    stairs: 0,
    noLift: false,
    extraStops: 0,
    isWeekend: false,
    hasFragile: false,
    hasLargeItems: false,
  });
  
  const [estimate, setEstimate] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const suburbSuggestions = useMemo(() => getAllSuburbNames(), []);

  const handleCalculate = () => {
    const result = calculateEstimate(formData);
    setEstimate(result);
    
    // Track calculation event
    trackWhatsAppClick({ 
      templateName: 'pricing_calculate', 
      page: 'pricing',
      serviceType: formData.serviceType 
    });
  };

  const handleWhatsAppQuote = () => {
    trackWhatsAppClick({ templateName: 'pricing', page: 'pricing' });
    const link = generateWhatsAppLink('pricing', {
      serviceType: formData.serviceType,
      pickup: formData.pickupSuburb,
      dropoff: formData.dropoffSuburb,
      estimate: estimate ? `R${estimate.estimate.min}-R${estimate.estimate.max}` : ''
    });
    window.open(link, '_blank');
  };

  const pricingFactors = [
    { title: 'Distance & Time', desc: 'Pickup to drop-off distance and estimated travel time', icon: MapPin },
    { title: 'Load Size', desc: 'Volume and weight of items being moved', icon: Package },
    { title: 'Helpers', desc: 'Additional helpers for heavy lifting and stairs', icon: CheckCircle },
    { title: 'Access', desc: 'Stairs, no lift, long carry distance, parking', icon: AlertCircle },
    { title: 'Waiting Time', desc: 'Time spent waiting at pickup or drop-off', icon: Info },
    { title: 'Extra Stops', desc: 'Multiple pickup or drop-off locations', icon: Truck }
  ];

  const serviceTiers = [
    { id: 'NOW', name: 'Rocket Van NOW', desc: 'Urgent on-demand', premium: '+50%', color: 'bg-red-500' },
    { id: 'TODAY', name: 'Rocket Van TODAY', desc: 'Same-day scheduled', premium: 'Base Rate', color: 'bg-[#039fff]' },
    { id: 'TOMORROW', name: 'Rocket Van TOMORROW', desc: 'Next-day scheduled', premium: '-10%', color: 'bg-green-500' }
  ];

  return (
    <div className="min-h-screen" data-testid="pricing-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <DollarSign className="h-16 w-16 text-[#039fff] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Pricing Made Simple</h1>
            <p className="text-xl text-gray-300 mb-4">
              Transparent pricing with no hidden fees
            </p>
            <p className="text-gray-400">
              Get an instant estimate below, then confirm exact pricing on WhatsApp
            </p>
          </div>
        </div>
      </section>

      {/* Quick Estimator */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-xl border-2 border-[#039fff]/20">
              <CardHeader className="text-center pb-2">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Calculator className="h-8 w-8 text-[#fb6402]" />
                  <CardTitle className="text-2xl text-[#001A47]">Quick Price Estimator</CardTitle>
                </div>
                <p className="text-gray-600">Get an instant estimate for your delivery or move</p>
              </CardHeader>
              <CardContent className="p-6 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column - Form */}
                  <div className="space-y-5">
                    {/* Service Type */}
                    <div>
                      <Label className="text-sm font-semibold">Service Type</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, serviceType: 'delivery', helpers: 'none'})}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            formData.serviceType === 'delivery' 
                              ? 'border-[#039fff] bg-[#039fff]/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid="service-delivery"
                        >
                          <Package className={`h-6 w-6 mx-auto mb-2 ${formData.serviceType === 'delivery' ? 'text-[#039fff]' : 'text-gray-400'}`} />
                          <span className={`text-sm font-medium ${formData.serviceType === 'delivery' ? 'text-[#039fff]' : 'text-gray-600'}`}>
                            Delivery
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, serviceType: 'miniMove'})}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            formData.serviceType === 'miniMove' 
                              ? 'border-[#fb6402] bg-[#fb6402]/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid="service-minimove"
                        >
                          <Truck className={`h-6 w-6 mx-auto mb-2 ${formData.serviceType === 'miniMove' ? 'text-[#fb6402]' : 'text-gray-400'}`} />
                          <span className={`text-sm font-medium ${formData.serviceType === 'miniMove' ? 'text-[#fb6402]' : 'text-gray-600'}`}>
                            Mini-Move
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Locations */}
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label htmlFor="pickup">Pickup Suburb</Label>
                        <Input
                          id="pickup"
                          placeholder="e.g., Stellenbosch"
                          value={formData.pickupSuburb}
                          onChange={(e) => setFormData({...formData, pickupSuburb: e.target.value})}
                          list="suburbs-list"
                          data-testid="pickup-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="dropoff">Drop-off Suburb</Label>
                        <Input
                          id="dropoff"
                          placeholder="e.g., Cape Town CBD"
                          value={formData.dropoffSuburb}
                          onChange={(e) => setFormData({...formData, dropoffSuburb: e.target.value})}
                          list="suburbs-list"
                          data-testid="dropoff-input"
                        />
                      </div>
                      <datalist id="suburbs-list">
                        {suburbSuggestions.map((suburb, i) => (
                          <option key={i} value={suburb} />
                        ))}
                      </datalist>
                    </div>

                    {/* Service Tier */}
                    <div>
                      <Label>When do you need it?</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {serviceTiers.map((tier) => (
                          <button
                            key={tier.id}
                            type="button"
                            onClick={() => setFormData({...formData, tier: tier.id})}
                            className={`p-3 rounded-lg border-2 text-center transition-all ${
                              formData.tier === tier.id
                                ? `border-[#039fff] bg-[#039fff]/5`
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <Badge className={`${tier.color} text-white text-xs mb-1`}>{tier.premium}</Badge>
                            <p className={`text-xs font-medium ${formData.tier === tier.id ? 'text-[#001A47]' : 'text-gray-600'}`}>
                              {tier.id}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Add-ons */}
                  <div className="space-y-5">
                    {/* Helpers (only for mini-move) */}
                    {formData.serviceType === 'miniMove' && (
                      <div>
                        <Label>Helpers</Label>
                        <select
                          value={formData.helpers}
                          onChange={(e) => setFormData({...formData, helpers: e.target.value})}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-2"
                          data-testid="helpers-select"
                        >
                          <option value="none">Driver only (R0)</option>
                          <option value="plus1">Driver + 1 helper (+R{addOnPricing.helpers.plus1})</option>
                          <option value="plus2">Driver + 2 helpers (+R{addOnPricing.helpers.plus2})</option>
                        </select>
                      </div>
                    )}

                    {/* Stairs */}
                    <div>
                      <Label>Floors/Stairs</Label>
                      <select
                        value={formData.stairs}
                        onChange={(e) => setFormData({...formData, stairs: parseInt(e.target.value)})}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-2"
                      >
                        <option value="0">Ground floor</option>
                        <option value="1">1 floor (+R{addOnPricing.stairs.perFloor})</option>
                        <option value="2">2 floors (+R{addOnPricing.stairs.perFloor * 2})</option>
                        <option value="3">3+ floors (+R{addOnPricing.stairs.perFloor * 3})</option>
                      </select>
                    </div>

                    {/* Toggle options */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">No lift access</p>
                          <p className="text-xs text-gray-500">+R{addOnPricing.stairs.noLiftSurcharge}</p>
                        </div>
                        <Switch
                          checked={formData.noLift}
                          onCheckedChange={(checked) => setFormData({...formData, noLift: checked})}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">Weekend delivery</p>
                          <p className="text-xs text-gray-500">+R{addOnPricing.weekendSurcharge}</p>
                        </div>
                        <Switch
                          checked={formData.isWeekend}
                          onCheckedChange={(checked) => setFormData({...formData, isWeekend: checked})}
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">Fragile items</p>
                          <p className="text-xs text-gray-500">+R{addOnPricing.fragileItems}</p>
                        </div>
                        <Switch
                          checked={formData.hasFragile}
                          onCheckedChange={(checked) => setFormData({...formData, hasFragile: checked})}
                        />
                      </div>

                      {formData.serviceType === 'miniMove' && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium">Large appliances</p>
                            <p className="text-xs text-gray-500">+R{addOnPricing.largeItems}</p>
                          </div>
                          <Switch
                            checked={formData.hasLargeItems}
                            onCheckedChange={(checked) => setFormData({...formData, hasLargeItems: checked})}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Calculate Button */}
                <Button
                  onClick={handleCalculate}
                  className="w-full mt-6 bg-[#039fff] hover:bg-[#0288e0] text-white py-6 text-lg"
                  data-testid="calculate-btn"
                >
                  <Calculator className="mr-2 h-5 w-5" />
                  Calculate Estimate
                </Button>

                {/* Results */}
                {estimate && (
                  <div className="mt-6 p-6 bg-gradient-to-r from-[#039fff]/10 to-[#fb6402]/10 rounded-xl border-2 border-[#039fff]" data-testid="estimate-result">
                    <div className="text-center mb-4">
                      <p className="text-sm text-gray-600 mb-2">Estimated Price Range</p>
                      <p className="text-5xl font-bold text-[#001A47]">
                        R{estimate.estimate.min} - R{estimate.estimate.max}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {estimate.suburbs.pickup} → {estimate.suburbs.dropoff}
                        {estimate.breakdown.distance > 0 && ` (~${Math.round(estimate.breakdown.distance)}km)`}
                      </p>
                    </div>

                    {/* Breakdown toggle */}
                    <button 
                      onClick={() => setShowBreakdown(!showBreakdown)}
                      className="text-sm text-[#039fff] hover:underline w-full text-center mb-4"
                    >
                      {showBreakdown ? 'Hide' : 'Show'} price breakdown
                    </button>

                    {showBreakdown && (
                      <div className="bg-white rounded-lg p-4 mb-4 text-sm">
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Base + Distance:</span>
                            <span className="font-medium">R{estimate.breakdown.basePrice}</span>
                          </div>
                          {estimate.breakdown.addOns > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Add-ons:</span>
                              <span className="font-medium">R{estimate.breakdown.addOns}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-600">Tier ({estimate.breakdown.tier}):</span>
                            <span className="font-medium">×{estimate.breakdown.tierMultiplier}</span>
                          </div>
                          <div className="border-t pt-2 flex justify-between font-bold">
                            <span>Estimated Total:</span>
                            <span>~R{estimate.estimate.midpoint}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Disclaimer */}
                    <div className="bg-white p-4 rounded-lg mb-4">
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="h-5 w-5 text-[#fb6402] flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-700">
                          <strong>Note:</strong> This is an estimate. Final price depends on exact addresses, 
                          item details, and real-time availability. Get your exact quote on WhatsApp.
                        </p>
                      </div>
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button 
                        onClick={handleWhatsAppQuote}
                        className="flex-1 bg-[#25D366] hover:bg-[#22c55e] text-white py-6"
                        data-testid="whatsapp-quote-btn"
                      >
                        <MessageCircle className="mr-2 h-5 w-5" />
                        Get Exact Quote on WhatsApp
                      </Button>
                      <Link to="/quote" className="flex-1">
                        <Button variant="outline" className="w-full py-6">
                          Use Quote Form
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How Pricing Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">How Pricing Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pricingFactors.map((factor, index) => {
                const Icon = factor.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="bg-[#039fff]/10 rounded-full p-3 w-12 h-12 flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-[#039fff]" />
                      </div>
                      <h3 className="font-bold text-[#001A47] mb-2">{factor.title}</h3>
                      <p className="text-sm text-gray-600">{factor.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Service Tiers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">Service Tiers</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {serviceTiers.map((tier) => (
                <Card key={tier.id} className="border-2 hover:border-[#039fff] transition-all text-center">
                  <CardContent className="p-8">
                    <Badge className={`${tier.color} text-white text-lg px-4 py-1 mb-4`}>
                      {tier.premium}
                    </Badge>
                    <h3 className="text-2xl font-bold text-[#001A47] mb-2">{tier.name}</h3>
                    <p className="text-gray-600">{tier.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-6">
              Exact availability depends on area and current capacity
            </p>
          </div>
        </div>
      </section>

      {/* Base Pricing Info */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">Typical Price Ranges</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 border-[#039fff]">
                <CardContent className="p-8 text-center">
                  <Package className="h-12 w-12 text-[#039fff] mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-[#001A47] mb-2">Local Delivery</h3>
                  <p className="text-4xl font-bold text-[#039fff] mb-2">R150 - R400</p>
                  <p className="text-sm text-gray-600">Single items, boxes, small furniture</p>
                  <ul className="text-left text-sm text-gray-600 mt-4 space-y-1">
                    <li>✓ Same-suburb: from R150</li>
                    <li>✓ Cross-town: R250-400</li>
                    <li>✓ Photo proof included</li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-[#fb6402]">
                <CardContent className="p-8 text-center">
                  <Truck className="h-12 w-12 text-[#fb6402] mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-[#001A47] mb-2">Mini-Move</h3>
                  <p className="text-4xl font-bold text-[#fb6402] mb-2">R450 - R1,500+</p>
                  <p className="text-sm text-gray-600">Room moves, multiple items, furniture</p>
                  <ul className="text-left text-sm text-gray-600 mt-4 space-y-1">
                    <li>✓ Studio/1 room: from R450</li>
                    <li>✓ With helpers: R600-1000+</li>
                    <li>✓ Moving blankets included</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-[#001A47] to-[#002B6B] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready for Your Exact Quote?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            The estimator gives you a ballpark. For accurate pricing, send us your details on WhatsApp.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8"
              onClick={handleWhatsAppQuote}
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Get Exact Quote on WhatsApp
            </Button>
            <Link to="/quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#001A47] px-8">
                Use Online Quote Form
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
